import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../lib/realtime';
import type { JobTemplateLineItem } from '../../data/jobTemplates';

export interface AdminSavedTemplate {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  estimated_minutes: number;
  priority: string;
  checklist: { id: string; label: string }[];
  line_items: JobTemplateLineItem[];
  tags: string[];
  source_template_id: string | null;
  use_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

async function log(biz: string, type: string, desc: string, eId: string, uid: string) {
  try {
    await supabase.from('activity_log').insert({
      business_id: biz, activity_type: type, description: desc,
      entity_type: 'job_template', entity_id: eId, logged_by: uid,
    });
  } catch { /* best effort */ }
}

export function useJobTemplatesAdmin(businessId: string | undefined, userId: string) {
  const [templates, setTemplates] = useState<AdminSavedTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('job_templates')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('use_count', { ascending: false });
      if (error) throw error;
      if (mountedRef.current) setTemplates((data ?? []) as AdminSavedTemplate[]);
    } catch { /* noop */ }
    finally { if (mountedRef.current) setLoading(false); }
  }, [businessId]);

  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  useRealtimeSubscription(businessId, ['job_templates'], fetch);

  const createTemplate = useCallback(async (data: Partial<AdminSavedTemplate>): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { data: row, error } = await supabase.from('job_templates').insert({
        business_id: businessId,
        name: data.name,
        description: data.description ?? null,
        estimated_minutes: data.estimated_minutes ?? 60,
        priority: data.priority ?? 'medium',
        checklist: data.checklist ?? [],
        line_items: data.line_items ?? [],
        tags: data.tags ?? [],
        source_template_id: data.source_template_id ?? null,
        created_by: userId,
      }).select('id').maybeSingle();
      if (error) return false;
      await log(businessId, 'template_created', `Template "${data.name}" created`, row?.id ?? '', userId);
      fetch();
      return true;
    } catch { return false; }
  }, [businessId, userId, fetch]);

  const updateTemplate = useCallback(async (id: string, data: Partial<AdminSavedTemplate>): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error } = await supabase.from('job_templates').update({
        name: data.name,
        description: data.description,
        estimated_minutes: data.estimated_minutes,
        priority: data.priority,
        checklist: data.checklist,
        line_items: data.line_items,
        tags: data.tags,
      }).eq('id', id);
      if (error) return false;
      await log(businessId, 'template_updated', `Template updated`, id, userId);
      fetch();
      return true;
    } catch { return false; }
  }, [businessId, userId, fetch]);

  const deleteTemplate = useCallback(async (id: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error } = await supabase.from('job_templates').update({ is_active: false }).eq('id', id);
      if (error) return false;
      await log(businessId, 'template_deleted', `Template deleted`, id, userId);
      fetch();
      return true;
    } catch { return false; }
  }, [businessId, userId, fetch]);

  return { templates, loading, refresh: fetch, createTemplate, updateTemplate, deleteTemplate };
}
